import React, { useRef } from 'react';
import _ from 'lodash';

import CommonModal from '../components/modals/common/Modal';
import {
  FormElement,
  FormList,
  FormListItem,
} from '../components/CommonFormComponents';
import { Description } from '../components/Description';
import {
  FIELD_WORK_HOUR_ZONES,
  PREFECTURE_LISTS,
  ONE_MASTER_FLAG,
  CALL_FROM_PAGE,
  CORPORATION_PERSONAL_NAME,
  CORPORATION_PERSONAL_KANA,
} from '../utilities/constants';
import LocalStorageService from '../services/utilities/LocalStorageService';
import { isPartnerCompanyRoleId } from '../utilities/roleUtility';
import {
  loadFromPartnerCompanies,
  loadFromFieldTreePage,
  loadFromAgencyRegister,
} from '../utilities/partnerCompanyRegisterUtil';
import { isNullOrEmpty } from '../utilities/stringUtil';
import {
  HEAD_OFFICE_OPTION,
  PartnerCompanyBranchDropdown,
} from './PartnerCompanyBranchDropdown';
import { getLabelPersonalCompanyType } from '../utilities/partnerCompanyUtil';
import { messageUtility } from '../utilities/messageUtility';

/** values:
 * buildee_use_flg: true = 登録済み
 * company_selected_counts: 利用実績数
 */
const PartnerCompanyRegisterConfirmDialog = props => {
  const {
    createCompanyFlag,
    events,
    loadFrom,
    validate,
    formValues,
    values,
    visible,
    workHourZoneSetting,
    isPartners,
    canProxyRegister,
    onSelect,
    resetIsSelectingCompany,
  } = props;
  const hasUsage = isPartners;

  const isRegistering = useRef(false); // 協力会社登録・追加ボタン連打防止対応

  const isPersonalCompany =
    values.company.personal_flag === ONE_MASTER_FLAG.personal.id;

  const fromPartnerCompanies = () => {
    return loadFromPartnerCompanies(loadFrom);
  };
  const fromFieldTreePage = () => {
    return loadFromFieldTreePage(loadFrom);
  };

  const fromAgencyRegister = () => {
    return loadFromAgencyRegister(loadFrom);
  };

  const dialogTitle = () => {
    if (createCompanyFlag) {
      if (fromPartnerCompanies()) {
        return '協力会社を新規登録';
      }
      if (fromFieldTreePage()) {
        return '協力会社を新規登録して現場体制に追加';
      }
      if (fromAgencyRegister()) {
        return '代行登録する会社を新規登録';
      }
    } else {
      if (fromPartnerCompanies()) {
        return '協力会社を新規追加';
      }
      if (fromFieldTreePage()) {
        return '現場体制に協力会社を追加';
      }
      if (fromAgencyRegister()) {
        return '代行登録する会社を追加';
      }
    }
    return '';
  };

  const addPartnerCompanyBtnName = () => {
    if (createCompanyFlag) {
      return fromFieldTreePage() ? '登録して現場に追加' : '登録';
    } else {
      return fromFieldTreePage() ? '現場に追加' : '協力会社に追加';
    }
  };

  const handleAddPartnerCompany = async () => {
    resetIsSelectingCompany();
    events.resetUpdatingFlag();
    if (isRegistering.current) {
      return;
    }
    isRegistering.current = true;
    if (createCompanyFlag) {
      // 新規登録
      try {
        await events.confirmCreatePartnerCompany();

        // 登録処理が終わったら連打禁止を解除
        isRegistering.current = false;
      } catch (error) {
        // エラーが発生したら連打禁止を解除
        isRegistering.current = false;
      }
    } else {
      events
        .submit()
        .then(response => {
          // 登録に失敗したら押下禁止を解除
          if (!response) {
            isRegistering.current = false;
          }
        })
        .catch(() => {
          // 登録中にエラーが発生したら押下禁止を解除
          isRegistering.current = false;
        });
    }
  };

  const handleCloseDialog = () => {
    resetIsSelectingCompany();
    events.close();
  };

  const handleCancelDialog = () => {
    resetIsSelectingCompany();
    if (
      fromFieldTreePage() &&
      !createCompanyFlag &&
      props.events.redirectFieldTree
    ) {
      props.events.redirectFieldTree();
    } else if (
      fromPartnerCompanies() &&
      !createCompanyFlag &&
      props.events.redirectPartnerCompanies
    ) {
      props.events.redirectPartnerCompanies();
    } else {
      events.close();
    }
  };

  const renderDialogDescriptionMessage = () => {
    if (createCompanyFlag) {
      if (fromPartnerCompanies() || fromAgencyRegister()) {
        return (
          <p>
            この内容で登録してよろしいですか？
            <br />
            この会社はBuildeeを利用したことがない会社です。
            <br />
            登録するとすべての元請会社が利用可能な会社となります。詳細は
            <a
              className='clickable-text'
              href='https://service.buildee.jp/support/agreement/'
              target='_blank'
              rel='noreferrer noopener'
            >
              [Buildee利用規約]
            </a>
            をご参照ください。
            <br />
            同じ名前の別会社を誤って追加しないように、住所や電話番号などが正しいかご確認ください。
          </p>
        );
      }

      if (fromFieldTreePage()) {
        return (
          <p>
            この会社はBuildeeを利用したことがない会社です。
            <br />
            登録するとすべての元請会社が利用可能な協力会社となります。詳細は
            <a
              className='clickable-text'
              href='https://service.buildee.jp/support/agreement/'
              target='_blank'
              rel='noreferrer noopener'
            >
              [Buildee利用規約]
            </a>
            をご参照ください。
            <br />
            同じ名前の別会社を誤って登録しないように、住所や電話番号などが正しいかご確認ください。
          </p>
        );
      }
    }

    if (hasUsage) {
      if (isPartnerCompanyRoleId(LocalStorageService.getRoleId())) {
        return (
          <React.Fragment>
            <p>
              この会社は【{values.prime_company_name}
              】で利用実績のある協力会社です。
              <br />
              別の会社を現場体制に追加する場合は再検索を行ってください。
            </p>
            <button
              className='btn--sub btn--description'
              onClick={handleCloseDialog}
            >
              協力会社を再検索
            </button>
          </React.Fragment>
        );
      }
      return (
        <React.Fragment>
          <p>
            この会社は自社内で利用実績のある協力会社です。
            <br />
            別の会社を現場体制に追加する場合は再検索を行ってください。
          </p>
          <button
            className='btn--sub btn--description'
            onClick={handleCloseDialog}
          >
            協力会社を再検索
          </button>
        </React.Fragment>
      );
    } else {
      if (fromPartnerCompanies()) {
        return (
          <React.Fragment>
            <p>
              この会社を自社の協力会社として追加してよろしいですか？
              <br />
              同じ名前の別会社を誤って追加しないように、住所や電話番号などが正しいかご確認ください。
              <br />
              別の会社を追加する場合は再検索を行ってください。
            </p>
            <button
              className='btn--sub btn--description'
              onClick={handleCloseDialog}
            >
              協力会社を再検索
            </button>
          </React.Fragment>
        );
      }
      if (fromFieldTreePage()) {
        if (
          isPartnerCompanyRoleId(LocalStorageService.getRoleId()) &&
          canProxyRegister()
        ) {
          return (
            <React.Fragment>
              <p>
                この会社は【{values.prime_company_name}
                】で利用実績のない協力会社です。
                <br />
                現場体制に追加すると、【{values.prime_company_name}
                】の協力会社一覧にも表示されます。
                <br />
                同じ名前の別会社を誤って追加しないように、住所や電話番号などが正しいかご確認ください。
                <br />
                別の会社を現場体制に追加する場合は再検索を行ってください。
              </p>
              <button
                className='btn--sub btn--description'
                onClick={handleCloseDialog}
              >
                協力会社を再検索
              </button>
            </React.Fragment>
          );
        }

        return (
          <React.Fragment>
            <p>
              この会社は自社内で利用実績のない協力会社です。
              <br />
              現場体制に追加すると、協力会社一覧にも表示されます。
              <br />
              同じ名前の別会社を誤って追加しないように、住所や電話番号などが正しいかご確認ください。
              <br />
              別の会社を現場体制に追加する場合は再検索を行ってください。
            </p>
            <button
              className='btn--sub btn--description'
              onClick={handleCloseDialog}
            >
              協力会社を再検索
            </button>
          </React.Fragment>
        );
      }
    }
  };

  const renderAddress = () => {
    const cityName = isNullOrEmpty(values.company.city_name)
      ? ''
      : values.company.city_name;
    const streetNumber = isNullOrEmpty(values.company.street_number)
      ? ''
      : values.company.street_number;
    const buildingName = isNullOrEmpty(values.company.building_name)
      ? ''
      : values.company.building_name;

    return (
      PREFECTURE_LISTS[values.company.prefectures_code_id] +
      cityName +
      streetNumber +
      buildingName
    );
  };

  return (
    <React.Fragment>
      {visible && (
        <CommonModal visible={visible}>
          <div
            className={
              'dialog dialog--modal PartnerCompanyRegisterConfirmDialog'
            }
          >
            <div className='dialog__header'>
              <span className='dialog__title'>{dialogTitle()}</span>
            </div>
            <div className='dialog__body PartnerCompanyRegisterConfirmDialog__body'>
              <Description type={hasUsage ? 'notice' : 'primary'}>
                {renderDialogDescriptionMessage()}
              </Description>
              <div className='PartnerCompanyRegisterConfirmDialog__confirm-form'>
                <FormList>
                  {fromFieldTreePage() && (
                    <React.Fragment>
                      <FormListItem>
                        <p className='text--small'>
                          この会社を現場体制に追加する場合は以下の項目を入力してください。
                        </p>
                      </FormListItem>
                      <FormListItem label='現場代理人'>
                        <FormElement>
                          <input
                            className='form-list__input-large'
                            id='representative'
                            type='text'
                            name='representative'
                            value={formValues.representative}
                            maxLength={255}
                            onChange={e => {
                              events.handleConfirmInput(
                                e.target.value,
                                'representative'
                              );
                            }}
                          />
                        </FormElement>
                      </FormListItem>
                      <FormListItem label='作業時間帯' required>
                        <FormElement validate={validate}>
                          {workHourZoneSetting.work_hour_zone_day_flag && (
                            <label className='checkbox'>
                              <input
                                type='checkbox'
                                onChange={e => {
                                  events.handleConfirmInput(
                                    e.target.checked
                                      ? FIELD_WORK_HOUR_ZONES.on.id
                                      : FIELD_WORK_HOUR_ZONES.off.id,
                                    'work_hour_zone_day_flag'
                                  );
                                }}
                                value={formValues.work_hour_zone_day_flag}
                                checked={
                                  formValues.work_hour_zone_day_flag === 1
                                }
                              />
                              <i className='i-square i-3x' />
                              昼間
                            </label>
                          )}
                          {workHourZoneSetting.work_hour_zone_night_flag && (
                            <label className='checkbox'>
                              <input
                                type='checkbox'
                                onChange={e => {
                                  events.handleConfirmInput(
                                    e.target.checked
                                      ? FIELD_WORK_HOUR_ZONES.on.id
                                      : FIELD_WORK_HOUR_ZONES.off.id,
                                    'work_hour_zone_night_flag'
                                  );
                                }}
                                value={formValues.work_hour_zone_night_flag}
                                checked={
                                  formValues.work_hour_zone_night_flag === 1
                                }
                              />
                              <i className='i-square i-3x' />
                              夜間
                            </label>
                          )}
                          {workHourZoneSetting.work_hour_zone_midnight_flag && (
                            <label className='checkbox'>
                              <input
                                type='checkbox'
                                onChange={e => {
                                  events.handleConfirmInput(
                                    e.target.checked
                                      ? FIELD_WORK_HOUR_ZONES.on.id
                                      : FIELD_WORK_HOUR_ZONES.off.id,
                                    'work_hour_zone_midnight_flag'
                                  );
                                }}
                                value={formValues.work_hour_zone_midnight_flag}
                                checked={
                                  formValues.work_hour_zone_midnight_flag === 1
                                }
                              />
                              <i className='i-square i-3x' />
                              深夜
                            </label>
                          )}
                        </FormElement>
                      </FormListItem>
                    </React.Fragment>
                  )}
                  <FormListItem label='種別'>
                    <FormElement>
                      {isPersonalCompany
                        ? ONE_MASTER_FLAG.personal.name
                        : ONE_MASTER_FLAG.corporation.name}
                    </FormElement>
                  </FormListItem>
                  {isPersonalCompany && (
                    <FormListItem
                      label={messageUtility.getMessage('I-000-186')}
                    >
                      <FormElement>
                        {getLabelPersonalCompanyType(
                          values.company.one_owner_flag
                        )}
                      </FormElement>
                    </FormListItem>
                  )}
                  <FormListItem
                    label={
                      isPersonalCompany
                        ? CORPORATION_PERSONAL_NAME.personal_name
                        : CORPORATION_PERSONAL_NAME.corporation_name
                    }
                  >
                    <FormElement>{values.company.name}</FormElement>
                  </FormListItem>
                  <FormListItem
                    label={
                      isPersonalCompany
                        ? CORPORATION_PERSONAL_KANA.personal_kana
                        : CORPORATION_PERSONAL_KANA.corporation_kana
                    }
                  >
                    <FormElement>
                      {values.company.kana ? values.company.kana : ''}
                    </FormElement>
                  </FormListItem>
                  {!!values.company.id && (
                    <FormListItem label='支店' required>
                      <FormElement>
                        <PartnerCompanyBranchDropdown
                          selectedCompanyId={values.company.id}
                          selectedPartnerCompanyBranchId={_.get(
                            values,
                            'branch.id',
                            HEAD_OFFICE_OPTION.id
                          )}
                          onSelect={onSelect}
                          previewValue={_.get(
                            values,
                            'branch.name',
                            HEAD_OFFICE_OPTION.name
                          )}
                          fixedOptions={[HEAD_OFFICE_OPTION]}
                          params={{ call_from: CALL_FROM_PAGE.FIELD_TREE_PAGE }}
                        />
                      </FormElement>
                    </FormListItem>
                  )}
                  <FormListItem label='住所'>
                    <FormElement>{renderAddress()}</FormElement>
                  </FormListItem>
                  <FormListItem label='電話番号'>
                    <FormElement>{values.company.tel}</FormElement>
                  </FormListItem>
                  {formValues.parent_tree_id === 0 && (
                    // 元請の配下の場合表示
                    <FormListItem label='別途業者'>
                      <FormElement>
                        <label className='checkbox'>
                          <input
                            type='checkbox'
                            onChange={e => {
                              events.handleConfirmInput(
                                e.target.checked ? 1 : 0,
                                'private_partner_company_flag'
                              );
                            }}
                            value={formValues.private_partner_company_flag}
                            checked={
                              formValues.private_partner_company_flag === 1
                            }
                          />
                          <i className='i-square i-3x' />
                          別途業者として登録する
                        </label>
                      </FormElement>
                    </FormListItem>
                  )}
                </FormList>
              </div>
            </div>
            <div className='dialog__footer PartnerCompanyRegisterConfirmDialog__footer'>
              <button className='btn--sub' onClick={handleCancelDialog}>
                {createCompanyFlag ? '登録情報を修正' : '追加をやめる'}
              </button>
              <button
                className='btn--effective'
                onClick={handleAddPartnerCompany}
              >
                {addPartnerCompanyBtnName()}
              </button>
            </div>
          </div>
        </CommonModal>
      )}
    </React.Fragment>
  );
};

export default PartnerCompanyRegisterConfirmDialog;



// WEBPACK FOOTER //
// ./src/components/PartnerCompanyRegisterConfirmDialog.jsx